import { Eventcalendar, MbscCalendarEvent, MbscPageLoadingEvent, Popup } from '@mobiscroll/react'; /* or import any other component */
import '@mobiscroll/react/dist/css/mobiscroll.min.css';
import { Backdrop, CircularProgress, Stack } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import Appointment from '../../../Models/Scheduling/Appointment.model';
import { appointmentStatusMap } from '../../../utils/mappings';
import MessageDisplay from '../../Shared/MessageDisplay';
import EventContent from '../EventContent';
import EventLabel from '../EventLabel';
import CalendarStatusLegend from '../Legends/CalendarStatusLegend';
import CalendarEventEditModal from '../Modals/CalendarEventEditModal';
import { cancelRecurringInstanceEvent, getCurrentUsersSchedule, mapEventData, updateEvent } from '../Services/CommonCalendarServices';
import EventTooltip from '../Tooltips/EventTooltip';
import { CalendarEventData } from '../types/CalendarEventData';
import ExportCalendarButton from '../Export/ExportCalendarButton';
import HelpDocumentIconButton from '../HelpDocumentIconButton';
import { setShowMessage } from '../../../store/shared.slice';
import { useAppDispatch } from '../../../store/store';
import RescheduleEventModal from '../Modals/RescheduleEventModal';
import { RescheduledNonRecurrenceDto } from '../../../Models/Scheduling/RescheduledNonRecurrenceDto.model';
import { RescheduledRecurrenceDto } from '../../../Models/Scheduling/RescheduledRecurrenceDto.model';
import { CreatedInstanceDto } from '../../../Models/Scheduling/CreatedInstanceDto.model';

const MySchedulesCalendar: React.FC<{ canCreate?: boolean; canEdit?: boolean; title?: string }> = (props) => {
    const calendarRef = React.useRef<Eventcalendar>(null);
    const dispatch = useAppDispatch();
    const [myEvents, setMyEvents] = useState([]);
    const [isEventEditorOpen, setIsEventEditorOpen] = useState<boolean>(false);
    const [mySelectedDate, setSelectedDate] = useState(new Date());
    const [hoveredEvent, setHoveredEvent] = useState<CalendarEventData | null>(null);
    const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
    const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
    const [selectedEvent, setSelectedEvent] = useState<CalendarEventData | null>(null);
    const [isBackdropOpen, setIsBackdropOpen] = useState<boolean>(false);
    const [monthBounds, setMonthBounds] = React.useState<{ firstDate: Date; lastDate: Date } | undefined>({ firstDate: new Date(), lastDate: new Date() });
    const [isRecurringRescheduleModalOpen, setIsRecurringRescheduleModalOpen] = useState<boolean>(false);
    const handlePageLoading = (event: MbscPageLoadingEvent) => {
        const { firstDay, lastDay } = event;
        updateBounds(firstDay, lastDay);
        setIsPopupOpen(false);
        setIsBackdropOpen(true);
        const firstDayMinusOneYear = new Date(firstDay);
        firstDayMinusOneYear.setFullYear(firstDayMinusOneYear.getFullYear() - 1);
        const lastDayPlusOneMonth = new Date(lastDay);
        lastDayPlusOneMonth.setMonth(lastDayPlusOneMonth.getMonth() + 1);
        getCurrentUsersSchedule(firstDayMinusOneYear, lastDayPlusOneMonth)
            .then((response) => {
                if (response.status === 200) {
                    const data = response.data.map(mapEventData);
                    setMyEvents(data);
                } else {
                    console.error('Error loading appointment data');
                    setMyEvents([]);
                }
            })
            .finally(() => setIsBackdropOpen(false));
    };

    const handleDeleteEventSuccess = (eventId: string) => {
        const newEvents = myEvents.filter((item) => item.Id !== eventId);
        setMyEvents(newEvents);
    };

    const handleUpdateEventSuccess = (event: Appointment | CalendarEventData) => {
        setMyEvents((currentEvents) => {
            return currentEvents.map((item) => {
                if (item.Id === event.Id) {
                    // TODO: clean this up so we don't have to map data every time we set the events
                    return mapEventData(event);
                } else {
                    return item;
                }
            });
        });
    };

    const handleCreateEventSuccess = (event: Appointment, rescheduledEvent?: CalendarEventData) => {
        setMyEvents((currentEvents) => {
            let newEvents = [...currentEvents, mapEventData(event)];
            if (rescheduledEvent !== undefined) {
                newEvents = newEvents.map((item) => {
                    if (item.Id === rescheduledEvent.id) {
                        rescheduledEvent.RescheduledAppointmentId = event.Id;
                        if (
                            rescheduledEvent.status !== appointmentStatusMap.Cancelled.value ||
                            rescheduledEvent.status !== appointmentStatusMap.NoCallNoShow.value
                        ) {
                            rescheduledEvent.status = appointmentStatusMap.Cancelled.value;
                        }
                        // TODO: clean this up so we don't have to map data every time we set the events
                        return mapEventData(rescheduledEvent);
                    } else {
                        return item;
                    }
                });
            }
            return newEvents;
        });
    };

    const handleSelectedDateChange = useCallback((event) => {
        setSelectedDate(event.date);
    }, []);

    const handleCloseEventEditor = () => {
        setIsEventEditorOpen(false);
    };
    const handleEventClick = (args: MbscCalendarEvent) => {
        const eventInfo = args.event as CalendarEventData;
        setSelectedEvent(eventInfo);
        setHoveredEvent(eventInfo);
        setAnchor(args.domEvent.target.parentElement);
        setIsPopupOpen(true);
    };

    const handleEventEdit = (eventData: CalendarEventData) => {
        setSelectedEvent(eventData);
        setIsPopupOpen(false);
        setIsEventEditorOpen(true);
    };

    const handleEventCreate = (event: MbscCalendarEvent) => {
        const eventInfo = event.event as CalendarEventData;
        setSelectedEvent(eventInfo);
        setIsPopupOpen(false);
        setIsEventEditorOpen(true);

        // We will add the event manually, returning false makes mobiscroll not add a placeholder.
        return false;
    };

    const handleCreateRecurrenceInstance = async (createdInstanceDto: CreatedInstanceDto) => {
        const { CreatedInstanceAppointment, OriginalRecurrenceAppointment } = createdInstanceDto;
        const mappedNewEvent = mapEventData(CreatedInstanceAppointment);
        const mappedOriginalEvent = mapEventData(OriginalRecurrenceAppointment);
        let newEvents = [
            mappedNewEvent,
            ...myEvents.map((item) => {
                if (item.Id === mappedOriginalEvent.Id) {
                    return mappedOriginalEvent;
                } else {
                    return item;
                }
            }),
        ];
        setMyEvents(newEvents);
    };
    const handleCancelEventClick = async (eventData: CalendarEventData) => {
        setIsBackdropOpen(true);
        if (eventData.recurring) {
            try {
                const response = await cancelRecurringInstanceEvent(eventData.original as Appointment, new Date(eventData.start));
                handleCreateRecurrenceInstance(response.data);
            } catch (error) {
                let errorMessage = 'An error occurred while cancelling the appointment.';
                if (error?.response?.data) {
                    errorMessage = `${errorMessage} \n\nReason: ${error.response.data}`;
                }
                dispatch(setShowMessage(true, errorMessage, 'error'));
            } finally {
                setIsBackdropOpen(false);
            }
            return;
        }
        eventData.status = appointmentStatusMap.Cancelled.value;
        try {
            const response = await updateEvent(eventData);
            if (response.data) {
                handleUpdateEventSuccess(response.data);
            }
        } catch (error) {
            let errorMessage = 'An error in loading the calendar has occurred. A response was given but data was not received with it.';
            if (error?.response?.data) {
                errorMessage = `${error}. \n\nReason: ${error.response.data}`;
            }
            setMyEvents([]);
            dispatch(setShowMessage(true, errorMessage, 'error'));
        } finally {
            setIsBackdropOpen(false);
        }
        handleUpdateEventSuccess(eventData);
    };

    const handleRescheduleEventClick = (eventData: CalendarEventData) => {
        if (eventData.recurring) {
            setIsRecurringRescheduleModalOpen(true);
            return;
        }

        if (eventData.status !== appointmentStatusMap.Cancelled.value && eventData.status !== appointmentStatusMap.NoCallNoShow.value) {
            eventData.status = appointmentStatusMap.Cancelled.value;
        }

        const newEvent: Partial<CalendarEventData> & { IsRescheduling: boolean } = {
            ...eventData,
            Id: undefined,
            status: appointmentStatusMap.Unconfirmed.value,
            AppointmentStatus: appointmentStatusMap.Unconfirmed.value,
            OriginalAppointmentId: eventData.Id,
            IsRescheduling: true,
        };

        setSelectedEvent(newEvent as any);
        setIsEventEditorOpen(true);
    };

    const updateBounds = useCallback(
        (firstDay: Date, lastDay: Date) => {
            const newBoundFirstDateTime = firstDay.getTime();
            const newBoundLastDateTime = lastDay.getTime();
            const monthBoundFirstDateTime = monthBounds.firstDate.getTime();
            const monthBoundLastDateTime = monthBounds.lastDate.getTime();
            if (newBoundFirstDateTime !== monthBoundFirstDateTime || newBoundLastDateTime !== monthBoundLastDateTime) {
                setMonthBounds({ firstDate: firstDay, lastDate: lastDay });
            }
        },
        [monthBounds.firstDate, monthBounds.lastDate]
    );

    useEffect(() => {
        document.title = props.title;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Stack spacing={1} direction={'row'}>
            <Stack spacing={1} justifyContent="flex-start" style={{ paddingTop: 10, padding: 5 }}>
                <HelpDocumentIconButton fullButton />
                <ExportCalendarButton
                    initialStartDate={monthBounds.firstDate}
                    initialEndDate={monthBounds.lastDate}
                    getEventsForExport={(startDate: Date, endDate: Date) => calendarRef.current.getEvents(startDate, endDate) ?? []}
                />
                <MessageDisplay />
                {selectedEvent !== undefined && selectedEvent !== null ? (
                    <>
                        <CalendarEventEditModal
                            open={isEventEditorOpen}
                            onSuccess={() => {
                                setIsEventEditorOpen(false);
                            }}
                            onUpdateEventSuccess={handleUpdateEventSuccess}
                            eventList={myEvents as any}
                            onCreateEventSuccess={handleCreateEventSuccess}
                            onRescheduleEvent={handleUpdateEventSuccess}
                            setLoadingOverlay={(isLoading: boolean) => setIsBackdropOpen(isLoading)}
                            onClose={handleCloseEventEditor}
                            eventData={selectedEvent}
                            memberId={null}
                        />
                        <RescheduleEventModal
                            eventData={selectedEvent}
                            setLoadingOverlay={(isLoading: boolean) => setIsBackdropOpen(isLoading)}
                            open={isRecurringRescheduleModalOpen}
                            onClose={() => {
                                setIsRecurringRescheduleModalOpen(false);
                            }}
                            onRescheduleSuccess={(rescheduledAppointment: RescheduledNonRecurrenceDto) => {
                                setIsRecurringRescheduleModalOpen(false);
                            }}
                            onRescheduleRecurrenceSuccess={(rescheduledRecurrence: RescheduledRecurrenceDto) => {
                                const { RescheduledAppointment, OriginalInstanceAppointment, OriginalRecurrenceAppointment } = rescheduledRecurrence;
                                const newEvents = myEvents
                                    .map((item) => {
                                        if (item.Id === OriginalRecurrenceAppointment.Id) {
                                            return mapEventData(OriginalRecurrenceAppointment);
                                        } else {
                                            return item;
                                        }
                                    })
                                    .concat([mapEventData(RescheduledAppointment), mapEventData(OriginalInstanceAppointment)]);
                                setMyEvents(newEvents);
                                setIsRecurringRescheduleModalOpen(false);
                            }}
                        />
                    </>
                ) : null}
                <CalendarStatusLegend direction="column" />
                <Backdrop open={isBackdropOpen} sx={{ zIndex: 1000 }}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            </Stack>

            <Eventcalendar
                ref={calendarRef}
                data={myEvents}
                clickToCreate={false}
                theme="material"
                height={880}
                width={'100%'}
                themeVariant="light"
                view={{ calendar: { labels: true } }}
                selectedDate={mySelectedDate}
                renderLabelContent={(event) => <EventLabel event={event} />}
                renderEventContent={(event) => <EventContent event={event} />}
                onPageLoading={handlePageLoading}
                onSelectedDateChange={handleSelectedDateChange}
                onEventClick={handleEventClick}
                onEventCreate={handleEventCreate}
            />
            <Popup
                display="anchored"
                isOpen={isPopupOpen}
                anchor={anchor}
                touchUi={false}
                showOverlay={false}
                contentPadding={false}
                closeOnOverlayClick={false}
                cssClass="md-tooltip"
                style={{ zIndex: 99 }}
            >
                <EventTooltip
                    hoveredEvent={hoveredEvent}
                    onMouseEnter={() => {}}
                    onMouseLeave={() => {}}
                    onEventCancelClick={handleCancelEventClick}
                    onDeleteEventSuccess={handleDeleteEventSuccess}
                    closePopup={() => setIsPopupOpen(false)}
                    onEventEditClick={(eventData: CalendarEventData) => {
                        handleEventEdit(eventData);
                        setIsPopupOpen(false);
                    }}
                    onEventRescheduleClick={handleRescheduleEventClick}
                />
            </Popup>
        </Stack>
    );
};

export default MySchedulesCalendar;
